/**
 * PipelineOverviewTab — pipeline metadata + a simple source → jobs → target
 * flow diagram. Read-only summary view (no editing) shown as the default
 * tab for a selected pipeline's detail page.
 */

import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import type { PEJob, PEPipeline } from "../../features/pipeline-engineer/types";
import { listJobs } from "../../features/pipeline-engineer/api/pe-client";
import { getConnectorIcon } from "../../features/pipeline-engineer/connectorIcons";

/** Date + time, no seconds (e.g. "Oct 7, 2026, 12:39 AM") — cleaner than the raw toLocaleString() default. */
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

interface PipelineOverviewTabProps {
  pipeline: PEPipeline;
  /** Bump to force a re-fetch of the job list (e.g. after a job is created elsewhere). */
  refreshKey?: number;
}

export function PipelineOverviewTab({
  pipeline,
  refreshKey = 0,
}: PipelineOverviewTabProps) {
  const [jobs, setJobs] = useState<PEJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setJobs(await listJobs(pipeline.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [pipeline.id]);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs, refreshKey]);

  return (
    <div className="glass-card rounded-2xl p-5 space-y-6">
      {/* Flow diagram */}
      <div>
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">
          Flow
        </h3>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2
              size={18}
              className="animate-spin text-white/30"
              aria-hidden="true"
            />
          </div>
        ) : error !== null ? (
          <p className="text-red-400 text-xs" role="alert">
            {error}
          </p>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <FlowNode label={pipeline.sourceType} sub="Source" />
            <ArrowRight
              size={16}
              className="text-white/20 shrink-0"
              aria-hidden="true"
            />
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-[120px]">
              {jobs.length === 0 ? (
                <span className="text-xs text-white/30 italic">
                  No jobs yet
                </span>
              ) : (
                jobs.map((job) => (
                  <span
                    key={job.id}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-white/70"
                    title={job.targetTable}
                  >
                    {job.jobName}
                  </span>
                ))
              )}
            </div>
            <ArrowRight
              size={16}
              className="text-white/20 shrink-0"
              aria-hidden="true"
            />
            <FlowNode label={pipeline.targetType} sub="Target" />
          </div>
        )}
      </div>

      {/* Metadata */}
      <div>
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">
          Details
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <MetaRow label="Name" value={pipeline.pipelineName} />
          <MetaRow
            label="Status"
            value={pipeline.isActive ? "Active" : "Inactive"}
          />
          <MetaRow label="Source Type" value={pipeline.sourceType} />
          <MetaRow label="Target Type" value={pipeline.targetType} />
          <MetaRow label="Job Count" value={String(jobs.length)} />
          <MetaRow
            label="Schedule"
            value={
              pipeline.scheduleEnabled
                ? `${pipeline.scheduleCron ?? "—"} (${pipeline.scheduleTimezone})`
                : "Disabled"
            }
          />
          {pipeline.scheduleEnabled && pipeline.scheduleNextRunAt && (
            <MetaRow
              label="Next Run"
              value={formatDateTime(pipeline.scheduleNextRunAt)}
            />
          )}
          {pipeline.scheduleLastRunAt && (
            <MetaRow
              label="Last Scheduled Run"
              value={`${formatDateTime(pipeline.scheduleLastRunAt)}${
                pipeline.scheduleLastRunStatus
                  ? ` — ${pipeline.scheduleLastRunStatus}`
                  : ""
              }`}
            />
          )}
          {pipeline.description && (
            <MetaRow label="Description" value={pipeline.description} />
          )}
        </dl>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FlowNode({ label, sub }: { label: string; sub: string }) {
  const { icon: Icon, colorClass } = getConnectorIcon(label);
  return (
    <div
      className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl border shrink-0 ${colorClass}`}
    >
      <Icon size={16} aria-hidden="true" />
      <span className="text-[11px] text-white/40">{sub}</span>
      <span className="text-xs font-medium text-white">{label}</span>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col min-w-0">
      <dt className="text-xs text-white/40">{label}</dt>
      <dd className="text-white/90 truncate">{value}</dd>
    </div>
  );
}
